<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useAbility } from "@casl/vue";

import PageHeader from "@/components/ui/headers/PageHeader.vue";
import { useActiveSubNavTitle } from "@/components/shared/navbar/useNavbarSubItems";
import BtnToRooms from "@/components/shared/BtnToRooms.vue";
import GeneralInfiniteScrollTable from "@/components/shared/table/GeneralInfiniteScrollTable.vue";
import TruncateTooltip from "@/components/shared/TruncateTooltip.vue";
import TableFilterTabs from "@/components/shared/table/TableFilterTabs.vue";
import PatientTableCellName from "@/components/models/patient/table/PatientTableCellName.vue";
import PatientTableCellAction from "@/components/models/patient/table/PatientTableCellAction.vue";

import {
  ECotreatmentTabs,
  useCooperatePracticeOptions,
  useCotreatmentDashboard,
  useCotreatmentTabFilters,
} from "@/composables/models/treatmentPlan/useCotreatmentDashboard.ts";
import type { TFilter } from "@/types/filter/tableFilters.types.ts";
import type {
  ICotreatmentDashboardFilters,
  TCooperatePracticeOption,
  TCotreatmentDashboardItem,
} from "@/api/models/treatmentPlan/cotreatmentDashboard";
import { ECooperateStaffType } from "@/api/models/treatmentPlan/treatmentPlan";
import { practiceSelectable } from "@/api/models/practice/practice";
import { ECooperatePartiesType } from "@/api/models/treatmentPlan/job";

import { formatDatetime, formatName } from "@/utils/Formatters.ts";
import { tableRowClick } from "@/utils/tableRowClick";
import FilterSelect from "@/components/ui/inputs/FilterSelect.vue";
import FilterDateRange from "@/components/ui/inputs/FilterDateRange.vue";
import { addPatientAction } from "@/components/ui/headers/pageHeaderActions.ts";

const { t } = useI18n();
const { can } = useAbility();
const router = useRouter();

definePage({
  meta: {
    title: "generic.entity.dashboard",
    privilegeScope: "section",
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.READ, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const headerActions = computed(() => addPatientAction(t, can));

const pageTitle = useActiveSubNavTitle();

const gpPracticeQuery = useCooperatePracticeOptions(ECooperatePartiesType.GP);
const orthoPracticeQuery = useCooperatePracticeOptions(ECooperatePartiesType.ORTHO);

// `practiceSelectable` gives the same title/subtitle(address)/value shape the rest
// of the app uses for practice selects. The option rows carry the practice — and
// its addresses — nested under `practice`.
const toPracticeItems = (options: TCooperatePracticeOption[] | undefined) =>
  (options ?? []).map((option) => practiceSelectable(option.practice));

const gpPracticeItems = computed(() => toPracticeItems(gpPracticeQuery.data.value));
const orthoPracticeItems = computed(() => toPracticeItems(orthoPracticeQuery.data.value));

// `TableFilterTabs` owns the `?tab=` url sync and preselects the first filter,
// so the page only holds the resulting tab.
const tabFilters = useCotreatmentTabFilters();
const activeTab = ref<TFilter<ECotreatmentTabs, ICotreatmentDashboardFilters> | null>(null);

const mobilePinnedKeys = computed<string[]>(() =>
  activeTab.value?.tab === ECotreatmentTabs.ACTIONS_NEEDED ? ["action"] : []
);

const headers = [
  {
    key: "patient",
    translationKey: "models.scheduling.addEditEventForm.patient",
    sortable: true,
    sortKey: "last_name",
    width: "22%",
  },
  {
    key: "action",
    translationKey: "models.patient.table.headers.action",
    sortable: false,
    width: "20%",
  },
  {
    key: "gp",
    translationKey: "models.patient.table.headers.linkedGP",
    sortable: false,
    width: "13%",
  },
  {
    key: "ortho",
    translationKey: "models.patient.table.headers.linkedORT",
    sortable: false,
    width: "13%",
  },
  {
    key: "last_update",
    translationKey: "models.patient.table.filter.lastUpdate",
    sortable: true,
    sortKey: "last_update",
    width: "14%",
  },
];

// Search/sort live on the query (`url` mode). UI filters are declared as query fields.
const query = useCotreatmentDashboard({
  url: { useQuery: true, headers },
  initial: { page_size: 25 },
  tabs: activeTab,
  // `TableFilterTabs` resolves the active tab (from `?tab=` or its first
  // filter) right after mount. Waiting for it avoids firing an extra request
  // with no tab filter, which would immediately be superseded.
  enabled: computed(() => activeTab.value !== null),
});

const tableInitialKeys = ["patient", "action", "last_update", "gp", "ortho"];

const staffOfType = (item: TCotreatmentDashboardItem, type: ECooperateStaffType) =>
  item.cooperate_staff?.find((link) => link.link_type === type)?.staff;

const practiceOfType = (item: TCotreatmentDashboardItem, type: ECooperatePartiesType) =>
  item.cooperate_practice?.find((link) => link.link_type === type)?.practice;

const onRowClick = async (event: MouseEvent, row: Record<"item", TCotreatmentDashboardItem>) => {
  if (row.item.patient?.id) {
    await tableRowClick(event, router, {
      name: "/rooms/patient/[id]",
      params: { id: row.item.patient.id },
    });
  }
};
</script>

<template>
  <div
    class="tw:relative"
    data-tour="dashboard-header"
  >
    <btn-to-rooms />

    <page-header
      :title="pageTitle"
      search-data-tour="dashboard-search"
      :search-placeholder="t('generic.tables.searchByNameOrEmail')"
      :actions="headerActions"
    >
      <template #filters>
        <filter-select
          v-model="query.f.assignedGpPractice"
          :items="gpPracticeItems"
          :label="t('models.patient.table.filter.assignedGpOffice')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[15%] tw:sm:min-w-[11.5rem]"
        />

        <filter-select
          v-model="query.f.assignedOrthoPractice"
          :items="orthoPracticeItems"
          :label="t('models.patient.table.filter.assignedOrthoOffice')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[15%] tw:sm:min-w-[11.5rem]"
        />

        <filter-date-range
          v-model="query.f.lastUpdate"
          field-name="last_update"
          :label="t('models.patient.table.filter.lastUpdate')"
          :max="new Date()"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[15%] tw:sm:min-w-[9.5rem]"
        />
      </template>
    </page-header>

    <div class="tw:rounded-[10px]">
      <div data-tour="dashboard-overview">
        <table-filter-tabs
          v-model:active-tab="activeTab"
          :filters="tabFilters"
          :loading="query.isLoading.value"
          query-key="tab"
        />
      </div>

      <div
        data-tour="dashboard-main-container"
        class="tw:rounded-[10px]"
      >
        <div class="tw:relative">
          <general-infinite-scroll-table
            :headers="headers"
            :initial-keys="tableInitialKeys"
            :query="query"
            :row-click="onRowClick"
            elevated-animation
            variant="dashboard"
            responsive
            mobile-layout="fields"
            :mobile-header-keys="['patient']"
            mobile-clickable
            data-test-id="cotreatment-table"
            :row-props="{
              'data-test-id': 'cotreatment-table-item',
              'data-tour': 'dashboard-patient-row',
            }"
            :prefetch-viewports="0.9"
            mobile-expandable
            :mobile-pinned-keys="mobilePinnedKeys"
          >
            <template #mobile-header="{ item }">
              <patient-table-cell-name
                :patient="item.patient"
                variant="mobile-header"
              />
            </template>

            <template #[`item.patient`]="{ item }">
              <patient-table-cell-name :patient="item.patient" />
            </template>

            <template #[`item.action`]="{ item }">
              <patient-table-cell-action :jobs="item.jobs_available_for_user" />
            </template>

            <template #[`item.last_update`]="{ item }">
              <div class="tw:text-xs">
                {{ formatDatetime(item.last_update) }}
              </div>
            </template>

            <template #[`item.gp`]="{ item }">
              <div v-if="staffOfType(item, ECooperateStaffType.GP)">
                <truncate-tooltip
                  :text="formatName(staffOfType(item, ECooperateStaffType.GP))"
                  :max-ch="18"
                  tooltip-location="top"
                />
                <truncate-tooltip
                  v-if="practiceOfType(item, ECooperatePartiesType.GP)"
                  :text="practiceOfType(item, ECooperatePartiesType.GP)?.name"
                  :max-ch="18"
                  tooltip-location="top"
                  class="tw:text-on-surface/60 tw:text-xs"
                />
              </div>
              <div v-else-if="practiceOfType(item, ECooperatePartiesType.GP)">
                <truncate-tooltip
                  :text="practiceOfType(item, ECooperatePartiesType.GP)?.name"
                  :max-ch="18"
                  tooltip-location="top"
                />
              </div>
              <div
                v-else
                class="tw:text-xs tw:text-on-surface/60"
              >
                {{ t("models.patient.table.noneLinkedGP") }}
              </div>
            </template>

            <template #[`item.ortho`]="{ item }">
              <div v-if="staffOfType(item, ECooperateStaffType.ORTHO)">
                <truncate-tooltip
                  :text="formatName(staffOfType(item, ECooperateStaffType.ORTHO))"
                  :max-ch="18"
                  tooltip-location="top"
                />
                <truncate-tooltip
                  v-if="practiceOfType(item, ECooperatePartiesType.ORTHO)"
                  :text="practiceOfType(item, ECooperatePartiesType.ORTHO)?.name"
                  :max-ch="18"
                  tooltip-location="top"
                  class="tw:text-on-surface/60 tw:text-xs"
                />
              </div>
              <div v-else-if="practiceOfType(item, ECooperatePartiesType.ORTHO)">
                <truncate-tooltip
                  :text="practiceOfType(item, ECooperatePartiesType.ORTHO)?.name"
                  :max-ch="18"
                  tooltip-location="top"
                />
              </div>
              <div
                v-else
                class="tw:text-xs tw:text-on-surface/60"
              >
                {{ t("models.patient.table.noneLinkedORT") }}
              </div>
            </template>
          </general-infinite-scroll-table>
        </div>
      </div>
    </div>
  </div>
</template>
